import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../lib/api';

interface Course {
 id: string;
 name: string;
 owner_id: string;
 [key: string]: any;
}

interface CourseContextType {
 courses: Course[];
 selectedCourseId: string | null;
 selectedCourse: Course | null;
 setSelectedCourseId: (courseId: string) => void;
 loading: boolean;
 error: string | null;
 refreshCourses: () => Promise<void>;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

export function CourseProvider({ children }: { children: ReactNode }) {
 const { user, loading: authLoading } = useAuth();
 const [courses, setCourses] = useState<Course[]>([]);
 const [selectedCourseId, setSelectedCourseIdState] = useState<string | null>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);

 // Get selected course object
 const selectedCourse = courses.find(c => c.id === selectedCourseId) || null;

 // Load courses from API
 const loadCourses = async () => {
 if (!user?.id) {
 console.log('⏭️ CourseContext: Skipping load (no user ID)');
 setCourses([]);
 setSelectedCourseIdState(null);
 setLoading(false);
 return;
 }

 try {
 console.log('📡 CourseContext: Loading courses for user:', user.id);
 console.log('🔍 API module:', api);
 setLoading(true);
 setError(null);
 
 console.log('🚀 About to call api.get("/api/owner/courses")');
 const data = await api.get('/api/owner/courses');
 console.log('📦 Received data:', data);
 const fetchedCourses = data.courses || [];
 console.log(`✅ CourseContext: Loaded ${fetchedCourses.length} courses`);
 setCourses(fetchedCourses);

 // Auto-select course logic
 if (fetchedCourses.length === 1) {
 // Auto-select the only course
 const courseId = fetchedCourses[0].id;
 console.log('🎯 Auto-selected course:', fetchedCourses[0].name);
 setSelectedCourseIdState(courseId);
 localStorage.setItem(`discgo_selected_course_${user.id}`, courseId);
 } else if (fetchedCourses.length > 1) {
 // Try to restore from localStorage
 const storedCourseId = localStorage.getItem(`discgo_selected_course_${user.id}`);
 if (storedCourseId && fetchedCourses.some(c => c.id === storedCourseId)) {
 setSelectedCourseIdState(storedCourseId);
 }
 // Otherwise leave it null to show selector
 }
 // If 0 courses, selectedCourseId stays null

 } catch (err: any) {
 console.error('❌ CourseContext error:', err);
 // Don't clear courses to [] on error — that would falsely trigger
 // the "Set up your course" screen. Keep whatever state we had.
 setError(err?.message || 'Failed to load courses');
 } finally {
 setLoading(false);
 }
 };

 // Load courses when user changes
 useEffect(() => {
 console.log('🔄 CourseContext: Effect triggered - authLoading:', authLoading, 'userId:', user?.id);
 if (!authLoading) {
 loadCourses();
 }
 }, [user?.id, authLoading]);

 // Set selected course and persist to localStorage
 const setSelectedCourseId = (courseId: string) => {
 if (!user?.id) return;
 
 setSelectedCourseIdState(courseId);
 localStorage.setItem(`discgo_selected_course_${user.id}`, courseId);
 
 // Update URL for deep linking
 const url = new URL(window.location.href);
 url.searchParams.set('courseId', courseId);
 window.history.replaceState({}, '', url.toString());
 };

 // Clear course selection on logout
 useEffect(() => {
 if (!user?.id && selectedCourseId) {
 setSelectedCourseIdState(null);
 }
 }, [user?.id]);

 const value = {
 courses,
 selectedCourseId,
 selectedCourse,
 setSelectedCourseId,
 loading,
 error,
 refreshCourses: loadCourses,
 };

 return <CourseContext.Provider value={value}>{children}</CourseContext.Provider>;
}

export function useCourse() {
 const context = useContext(CourseContext);
 if (context === undefined) {
 throw new Error('useCourse must be used within a CourseProvider');
 }
 return context;
}
